/**
 * Lead-detail generator — produces a full `GetLeadResponse` for the `get_lead`
 * N8N action (types/n8nResponses.ts:222). This is the heaviest object in the
 * schema: lvl2 enriched data + ICP block + personalization strategy + 3-touch
 * outreach messages per channel + lvl1 person/company.
 *
 * The lead card drives the identity so the detail view always matches the card
 * the user clicked. Channel-aware: only channels the lead is enriched for get
 * outreach message bodies.
 */

import type {
  LeadCardResponse,
  GetLeadResponse,
  LeadLvl2Data,
  LeadLvl1PersonData,
  LeadLvl1CompanyData,
  OutreachMessage,
  PersonalizationStrategy,
} from '../../../types/n8nResponses';
import { fill, SUBJECT_LINES, OUTREACH_BODIES, PAINS, TRIGGERS, VALUE_HOOKS, PROOFS } from '../dictionaries';

const toIsoDaysAgo = (days: number): string => {
  const base = new Date('2026-07-01T12:00:00Z').getTime();
  return new Date(base - days * 86_400_000).toISOString();
};

/** Which channels have outreach touches, based on what the card was enriched for. */
const activeChannels = (card: LeadCardResponse): { email: boolean; linkedin: boolean; whatsapp: boolean; phone: boolean } => ({
  email: Boolean(card.person_email),
  linkedin: Boolean(card.linkedin_id),
  whatsapp: Boolean(card.whatsapp_id),
  phone: Boolean(card.phone_id),
});

const buildOutreachMessages = (
  card: LeadCardResponse,
): Pick<LeadLvl2Data, 'email_message_1' | 'email_outreached_1' | 'email_message_2' | 'email_outreached_2' | 'email_message_3' | 'email_outreached_3'> => {
  const vars = {
    firstName: card.person_name.split(' ')[0] || 'there',
    company: card.company_name || 'your company',
    industry: card.company_industry || 'your industry',
    trigger: TRIGGERS[card.lead_id.charCodeAt(0) % TRIGGERS.length],
    proof: PROOFS[card.lead_id.charCodeAt(1) % PROOFS.length || 0],
  };
  return {
    email_message_1: {
      subject: fill(SUBJECT_LINES[card.lead_id.charCodeAt(2) % SUBJECT_LINES.length], vars),
      emailBody: fill(OUTREACH_BODIES[0], vars),
    } satisfies OutreachMessage,
    email_outreached_1: toIsoDaysAgo(8),
    email_message_2: { emailBody: fill(OUTREACH_BODIES[1], vars) } satisfies OutreachMessage,
    email_outreached_2: toIsoDaysAgo(4),
    email_message_3: { emailBody: fill(OUTREACH_BODIES[2], vars) } satisfies OutreachMessage,
    email_outreached_3: null,
  };
};

const buildPersonalizationStrategy = (card: LeadCardResponse): PersonalizationStrategy => ({
  language_strategy: {
    primary_language_detection: {
      native_language: 'English',
      native_confidence: 0.92,
      location: card.country || 'Unknown',
      location_language: 'English',
    },
    context_analysis: {
      company_scope: 'B2B',
      company_primary_market: card.company_industry || 'Technology',
      person_content_language: 'English',
      person_linkedin_language: 'English',
    },
    decision_logic: {
      selected_language: 'English',
      selection_reason: 'Business context overrides location.',
      business_context_override: true,
      confidence: 0.9,
    },
    outreach_language: 'English',
    subject_line_language: 'English',
    body_language: 'English',
    cta_language: 'English',
    language_rationale: 'Lead operates internationally; English maximizes reply odds.',
    fallback_plan: 'Switch to native language if no reply after touch 2.',
    validation_checkpoint: {
      question: 'Does the lead reply in English?',
      if_yes: 'Continue in English.',
      if_no: 'Switch language on next touch.',
      current_answer: 'pending',
    },
    localization_requirements: ['Local timezone-aware send times', 'Currency in lead\'s region'],
    cultural_adaptation: {
      greeting: 'Hi {firstName}',
      tone: 'Direct, value-led',
      proof_positioning: 'Lead with peer proof',
      cta_style: 'Low-friction question',
    },
  },
  opening_approach: `Reference ${card.company_industry || 'their industry'} pain in the first line.`,
  language_style: ['Concise', 'Concrete numbers', 'No jargon'],
  avoid_language: ['Generic "hope you\'re well"', 'Feature dumps'],
  proof_positioning: PROOFS[0],
  call_to_action: 'A 15-minute walkthrough — any value?',
  follow_up_cadence: '3 touches: day 1, day 4, day 8.',
  channel_priority: 'Email → LinkedIn → WhatsApp',
  personalization_hooks: [
    PAINS[card.lead_id.charCodeAt(3) % PAINS.length || 0],
    VALUE_HOOKS[card.lead_id.charCodeAt(4) % VALUE_HOOKS.length || 0],
  ],
  conversation_starters: [
    `Noticed ${card.company_name} is scaling — is research bandwidth the bottleneck?`,
    'What does your current reply rate look like?',
  ],
  objection_preemption: 'If timing is off, I can follow up next quarter — no pressure.',
});

/**
 * Build the full GetLeadResponse for a lead card. Deterministic given the card
 * (every field derives from card.lead_id / person / company), so re-opening a
 * lead yields the same detail.
 */
export const generateMockLeadDetail = (card: LeadCardResponse): GetLeadResponse => {
  const channels = activeChannels(card);
  const outreach = buildOutreachMessages(card);
  const isPerson = card.lvl1_person ? true : true; // mock leads are always persons
  const lvl1PersonId = (card.lvl1_person?.id as number) ?? 10000;
  const companyLvl1Id = (card.lvl1_company?.id as number) ?? 1000;
  const companyDomain = card.company_website?.replace(/^https?:\/\//, '') || 'example.com';

  const lvl2: LeadLvl2Data = {
    id: lvl1PersonId,
    lvl1_id_client: `${lvl1PersonId}`,
    client: card.person_name,
    company_lvl1_id: companyLvl1Id,
    external_id: card.linkedin_id,
    type: 'person',
    icp_score: card.icp_score,
    profile_completeness: Math.min(100, 40 + card.icp_score / 2),
    status: card.status,
    lead_substatus: card.lead_substatus ?? null,
    substatus: card.lead_substatus ?? null,
    is_customer: card.status === 'converted' ? 1 : 0,

    icp: {
      ICP_fit: card.icp_fit === 'ideal' ? 'ideal'
        : card.icp_fit === 'good' ? 'high'
        : card.icp_fit === 'moderate' ? 'moderate'
        : 'low',
      ICP_score: card.icp_score,
      confidence_score: 0.8,
      top_pain: PAINS[card.lead_id.charCodeAt(3) % PAINS.length || 0],
      trigger: TRIGGERS[card.lead_id.charCodeAt(0) % TRIGGERS.length],
      value_hook: VALUE_HOOKS[card.lead_id.charCodeAt(4) % VALUE_HOOKS.length || 0],
      proof: PROOFS[card.lead_id.charCodeAt(1) % PROOFS.length || 0],
      primary_services: card.company_industry || 'SaaS',
    },

    name: card.person_name,
    overview: `${card.person_role || 'Leader'} at ${card.company_name || 'a company'} in ${card.company_industry || 'tech'}.`,
    personalization_strategy: buildPersonalizationStrategy(card),
    preferred_language: 'English',

    lon: null,
    lat: null,
    country: card.country || 'Unknown',
    state: null,
    city: card.city || null,
    full_address: card.city ? `${card.city}, ${card.country}` : null,

    profile_picture: null,

    role_position: card.person_role || null,
    timezone: (card.lvl1_person as any)?.timezone || null,
    nr_employees: card.nr_employees != null ? String(card.nr_employees) : null,
    industry: card.company_industry || null,
    account_manager_id: null,
    interaction_summary: card.status === 'engaged' || card.status === 'proposal' || card.status === 'converted'
      ? 'Lead has engaged with outreach — reply received, conversation in progress.'
      : null,
    notes: null,
    isr_notes: JSON.stringify({
      schema_version: 1,
      activities: [
        {
          type: 'status_change',
          actor: 'ai-agent-1',
          timestamp: toIsoDaysAgo(2),
          from_status: 'enriched',
          to_status: card.status,
          from_substatus: null,
          to_substatus: card.lead_substatus ?? null,
        },
      ],
      current_status: card.status,
      current_substatus: card.lead_substatus ?? null,
      icp_score: card.icp_score,
      last_updated: toIsoDaysAgo(1),
    }),
    notes_json: null,

    campaign_id: card.campaign_id,
    generator_id: null,
    replaces: card.replaces ?? null,
    replaces_reason: card.replaces_reason ?? null,

    // Channel ids + enrichment timestamps. Only enriched channels get timestamps.
    discord_id: null, discord_enriched: null,
    email_id: channels.email ? card.person_email : null,
    email_enriched: channels.email ? toIsoDaysAgo(10) : null,
    facebook_id: null, facebook_enriched: null,
    generator_enriched: toIsoDaysAgo(12),
    github_id: null, github_enriched: null,
    google_places_id: null, google_places_enriched: null,
    instagram_id: null, instagram_enriched: null,
    line_id: null,
    linkedin_id: channels.linkedin ? card.linkedin_id : null,
    linkedin_enriched: channels.linkedin ? toIsoDaysAgo(9) : null,
    linkedin_summary: channels.linkedin ? `Professional Snapshot: ${card.person_name} leads ${card.company_name}'s ${card.company_industry || 'go-to-market'} efforts.` : null,
    messenger_id: null,
    mobile_id: channels.phone ? card.phone_id : null,
    pinterest_id: null, pinterest_enriched: null,
    phone_id: channels.phone ? card.phone_id : null,
    phone_enriched: channels.phone ? toIsoDaysAgo(8) : null,
    research_enriched: toIsoDaysAgo(7),
    tiktok_id: null, tiktok_enriched: null,
    tripadvisor_id: null, tripadvisor_enriched: null,
    twitter_x_id: null, twitter_x_enriched: null,
    website_id: channels.email ? '1' : null,
    website_enriched: channels.email ? toIsoDaysAgo(10) : null,
    wechat_id: null,
    whatsapp_id: channels.whatsapp ? card.whatsapp_id : null,
    whatsapp_enriched: channels.whatsapp ? toIsoDaysAgo(6) : null,
    youtube_id: null, youtube_enriched: null,

    magnet_asset: 'Case study — 3x reply rate',
    agent_id: 'ai-agent-1',
    agent_personality: 'Consultative, concise',
    current_hat: 'SDR',

    // 3-touch email sequence (the primary channel). Other channels share the
    // same body shape with null messages where the lead isn't enriched.
    ...outreach,
    whatsapp_message_1: channels.whatsapp ? 'Hi {firstName}, quick idea for {company} outbound?' : null,
    whatsapp_outreached_1: channels.whatsapp ? toIsoDaysAgo(7) : null,
    linkedin_message_1: channels.linkedin ? 'Hi {firstName}, saw your team scaling — worth connecting?' : null,
    linkedin_outreached_1: channels.linkedin ? toIsoDaysAgo(7) : null,
    phone_message_1: null, phone_outreached_1: null,
    twitter_x_message_1: null, twitter_x_outreached_1: null,
    instagram_message_1: null, instagram_outreached_1: null,
    messenger_message_1: null, messenger_outreached_1: null,
    tiktok_message_1: null, tiktok_outreached_1: null,
    line_message_1: null, line_outreached_1: null,
    sms_message_1: null, sms_outreached_1: null,

    whatsapp_message_2: null, whatsapp_outreached_2: null,
    linkedin_message_2: null, linkedin_outreached_2: null,
    line_message_2: null, line_outreached_2: null,
    phone_message_2: null, phone_outreached_2: null,
    twitter_x_message_2: null, twitter_x_outreached_2: null,
    instagram_message_2: null, instagram_outreached_2: null,
    messenger_message_2: null, messenger_outreached_2: null,
    tiktok_message_2: null, tiktok_outreached_2: null,
    sms_message_2: null, sms_outreached_2: null,

    whatsapp_message_3: null, whatsapp_outreached_3: null,
    linkedin_message_3: null, linkedin_outreached_3: null,
    line_message_3: null, line_outreached_3: null,
    phone_message_3: null, phone_outreached_3: null,
    twitter_x_message_3: null, twitter_x_outreached_3: null,
    instagram_message_3: null, instagram_outreached_3: null,
    messenger_message_3: null, messenger_outreached_3: null,
    tiktok_message_3: null, tiktok_outreached_3: null,
    sms_message_3: null, sms_outreached_3: null,

    last_communication: card.status === 'engaged' || card.status === 'proposal' || card.status === 'converted' ? toIsoDaysAgo(2) : null,
    dont_contact: card.status === 'discarded' ? 'Out of ICP' : null,
    likelihood: card.status === 'converted' ? 'high' : card.status === 'engaged' ? 'medium' : 'low',

    created_At: card.created_at,
  };

  const lvl1_person: LeadLvl1PersonData = {
    id: lvl1PersonId,
    status: card.status,
    name: card.person_name,
    type: 'person',
    company_lvl1_id: companyLvl1Id,
    account_manager_id: null,
    profile_completeness: lvl2.profile_completeness,
    email_id: channels.email ? card.person_email : null,
    phone_id: channels.phone ? card.phone_id : null,
    mobile_id: channels.phone ? card.mobile_id : null,
    whatsapp_id: channels.whatsapp ? card.whatsapp_id : null,
    email: channels.email ? card.person_email : null,
    phone: channels.phone ? card.person_phone : null,
    mobile: channels.phone ? card.person_mobile : null,
    notes: null,
    isr_notes: lvl2.isr_notes,
    notes_json: null,

    country: card.country || 'Unknown',
    country_code: (card.lvl1_person as any)?.country_code || 'XX',
    state: null,
    postal_code: null,
    city: card.city || null,
    neighborhood: null,
    full_address: card.city ? `${card.city}, ${card.country}` : null,
    lat: null,
    lon: null,
    plus_code: null,
    timezone: (card.lvl1_person as any)?.timezone || null,

    profile_picture: null,

    industry: card.company_industry || null,
    department: null,
    role_position: card.person_role || null,
    seniority: card.person_seniority || null,
    nr_employees: card.nr_employees != null ? String(card.nr_employees) : null,

    summary: `${card.person_name} is ${card.person_role || 'a leader'} at ${card.company_name}, a ${card.company_industry || 'tech'} company.`,
    linkedin_summary: channels.linkedin ? lvl2.linkedin_summary : null,
    opportunities: JSON.stringify({ expansion: card.company_industry, hiring: true }),
    preferred_language: 'English',
    profile_intelligence: JSON.stringify({
      pains: [PAINS[0]],
      triggers: [TRIGGERS[0]],
    }),
    recent_activities: JSON.stringify([
      { type: 'post', when: toIsoDaysAgo(5), summary: 'Posted about scaling their SDR team.' },
    ]),
  };

  const lvl1_company: LeadLvl1CompanyData = {
    id: companyLvl1Id,
    status: 'enriched',
    name: card.company_name || 'Unknown',
    type: 'company',
    account_manager_id: null,
    email_id: channels.email ? card.company_email : null,
    phone_id: channels.phone ? card.phone_id : null,
    mobile_id: channels.phone ? card.mobile_id : null,
    whatsapp_id: channels.whatsapp ? card.whatsapp_id : null,
    website_id: channels.email ? '1' : null,
    email: channels.email ? card.company_email : null,
    phone: channels.phone ? card.person_phone : null,
    mobile: channels.phone ? card.person_mobile : null,

    country: card.country || 'Unknown',
    country_code: (card.lvl1_person as any)?.country_code || 'XX',
    state: null,
    postal_code: null,
    city: card.city || null,
    neighborhood: null,
    full_address: card.city ? `${card.city}, ${card.country}` : null,
    lat: null,
    lon: null,
    plus_code: null,
    timezone: (card.lvl1_person as any)?.timezone || null,

    industry: card.company_industry || null,
    nr_employees: card.nr_employees != null ? String(card.nr_employees) : null,
    founded_year: String(2010 + (parseInt(card.lead_id, 10) % 14)),

    website: card.company_website || null,
    linkedin_url: card.company_linkedin || null,

    logo_url: null,

    description: `${card.company_name} is a ${card.company_industry || 'technology'} company.`,
    business_model: 'B2B SaaS',
    revenue_range: card.revenue || null,
    cnae_code: null,
    cnae_text: null,
    birthdate: null,
    continent: null,
    size_range: card.size_range || null,
    revenue: card.revenue || null,
    business_database_summary: null,
    funding_status: card.status === 'converted' ? 'Profitable' : 'Series B',

    twitter_url: null,
    facebook_url: null,
    instagram_url: null,
    youtube_url: null,

    company_intelligence: JSON.stringify({ market: card.company_industry, growth: 'scaling' }),
    key_people: JSON.stringify([{ name: card.person_name, role: card.person_role }]),
    recent_news: JSON.stringify([{ when: toIsoDaysAgo(20), summary: 'Announced expansion.' }]),
    summary: `${card.company_name} — ${card.company_industry || 'tech'}, ${card.nr_employees || '50-200'} employees.`,
    linkedin_summary: `${card.company_name} is scaling its ${card.company_industry || 'go-to-market'} team.`,
  };

  void isPerson;
  void companyDomain;

  return { lvl2, lvl1_person, lvl1_company } satisfies GetLeadResponse;
};
